import { useState } from 'react';
import { IDataPackage, IDisallowedExtensionsResult } from '@vivli/shared/features/data-package/infrastructure/interface';
import { first } from 'rxjs/operators';
import { DataPackageStatusEnum, DataPackageTypeEnum } from '@vivli/features/studies/infrastructure/enum';
import { useResultRequestsFileInspection } from '@vivli/features/results-request/infrastructure/hook';
import { useDataPackageApiContext, useDataPackageFileApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';
import { DataPackageV2Component } from '@vivli/shared/features/data-package/components';

interface ResultRequestDataPackageFeatureProps {
    dataPackageId: string;
    onPackageSubmitted: (dataPackage: IDataPackage) => void;
    resultRequestId: string;
    dataRequestId: string;
}

export const ResultRequestDataPackageFeature = ({
    dataPackageId,
    onPackageSubmitted,
    resultRequestId,
    dataRequestId
}: ResultRequestDataPackageFeatureProps) => {
    const [submittingDataPackage, setSubmittingDataPackage] = useState(false);
    const [okToSubmit, setOkToSubmit] = useState(null);
    const dataPackageService = useDataPackageApiContext();
    const dataPackageFileService = useDataPackageFileApiContext();
    const modalService = useModalApiContext();
    const { allFilesAllowed, confirmAndSubmit } = useResultRequestsFileInspection(onPackageSubmitted, setSubmittingDataPackage);

    const handleBadFileExtensionList = (disallowedExtensions: IDisallowedExtensionsResult, dataPackage: IDataPackage) => {
        if (!allFilesAllowed(disallowedExtensions, false)) {
            setOkToSubmit(false);
            return;
        }

        const zipFiles = dataPackage.dataPackageFileDescriptors.filter((f) => f.name?.toLowerCase().endsWith('.zip'));
        if (!zipFiles || zipFiles.length === 0) {
            setOkToSubmit(true);
            confirmAndSubmit(dataRequestId, dataPackage, resultRequestId);
        } else {
            const numZipFiles = zipFiles.length;
            let processedZipFiles = 0;
            let stopProcessing = false;
            zipFiles?.forEach((zipFile) => {
                if (stopProcessing) return; // skip remaining iterations once a bad zip file detected.
                const localFile = dataPackage.dataPackageFileDescriptors.find((f) => f.name === zipFile.name);
                dataPackageFileService
                    .getBadExtensionFileList(dataPackage.id, localFile.name, true)
                    .pipe(first())
                    .subscribe(
                        (disallowedExtensions) => {
                            processedZipFiles++;
                            if (!allFilesAllowed(disallowedExtensions, true)) {
                                stopProcessing = true; // prevent any further iterations from starting processing
                                return;
                            } else {
                                // all good, proceed with submission if this is the last zip file to process
                                if (processedZipFiles === numZipFiles && !stopProcessing) {
                                    setOkToSubmit(true);
                                    confirmAndSubmit(dataRequestId, dataPackage, resultRequestId);
                                }
                            }
                        },
                        (error) => {
                            dataPackageService.handleSubmitErrorWithFileCheck(error, true, 'Result Request');
                        }
                    );
            });
        }
    };

    const handlePackageSubmitted = (dataPackage: IDataPackage) => {
        // for zip files uploaded to request results package, check for safe files within zip - package
        // type + descriptors presence should always be true but just in case
        if (dataPackage?.dataPackageType === DataPackageTypeEnum.ResearchVMResults && dataPackage?.dataPackageFileDescriptors?.length > 0) {
            {
                dataPackageFileService
                    .getBadExtensionFileListForDataPackage(dataPackage.id, true)
                    .pipe(first())
                    .subscribe({
                        next: (disallowedExtensions) => handleBadFileExtensionList(disallowedExtensions, dataPackage),
                        error: (error) => dataPackageService.handleSubmitErrorWithFileCheck(error, true, 'Result Request')
                    });
            }
        }
    };

    return (
        <DataPackageV2Component
            dataPackageId={dataPackageId}
            allowUpload={(dataPackage) => dataPackage.status === DataPackageStatusEnum.Draft}
            allowSubmit={true}
            allowDelete={true}
            onPackageSubmit={handlePackageSubmitted}
            disableSubmit={submittingDataPackage}
        />
    );
};
